// Client helpers for admin-approved prorated late enrollment.
//
// SINGLE AUTHORITY: this file never computes or stores a price. The
// `admin-prorated-enrollment` edge function owns the calculation and the
// authorization record; `create-class-checkout` independently re-resolves the
// approved amount at checkout time. The browser can only ask and display.

import { supabase } from "@/integrations/supabase/client";

export interface ProrationMeeting {
  date: string;
  status: "completed" | "remaining" | "cancelled";
}

export interface ProrationCalculation {
  ok: boolean;
  blockers: string[];
  warnings: string[];
  class_slug: string;
  class_title: string;
  session_label: string;
  normal_price_cents: number;
  per_meeting_cents: number;
  calculated_price_cents: number;
  total_meetings: number;
  cancelled_meetings: number;
  chargeable_meetings: number;
  completed_meetings: number;
  remaining_meetings: number;
  today: string;
  meetings: ProrationMeeting[];
}

export interface ProratedAuthorization {
  id: string;
  parent_email: string;
  parent_name: string | null;
  class_slug: string;
  class_title: string | null;
  session_label: string;
  normal_price_cents: number;
  calculated_price_cents: number;
  approved_price_cents: number;
  override_reason: string | null;
  total_meetings: number;
  completed_meetings: number;
  cancelled_meetings: number;
  remaining_meetings: number;
  per_meeting_cents: number;
  max_children: number;
  status: "active" | "reserved" | "used" | "expired" | "cancelled";
  approved_at: string;
  expires_at: string;
  consumed_at: string | null;
  consumed_payment_id: string | null;
  admin_notes: string | null;
}

export const BLOCKER_MESSAGES: Record<string, string> = {
  class_not_found: "This class could not be found.",
  class_not_open: "This class isn't open for registration, so a prorated price can't be approved.",
  class_external: "This class is run by an outside provider — families pay the provider directly.",
  class_free: "This class is free, so there is nothing to prorate.",
  session_not_found: "That session isn't listed on this class.",
  session_closed: "That session is closed to new registrations.",
  no_schedule: "This session has no confirmed meeting dates yet, so proration can't be calculated.",
  no_remaining_meetings: "Every meeting in this session has already happened.",
  no_chargeable_meetings: "Every meeting in this session is cancelled.",
  session_price_missing: "This session has no price set.",
};

export function money(cents: number): string {
  const sign = cents < 0 ? "-" : "";
  return `${sign}$${(Math.abs(cents) / 100).toFixed(2)}`;
}

export function parseDollarsToCents(input: string): number | null {
  const t = String(input ?? "").trim().replace(/^\$/, "").replace(/,/g, "");
  if (!t) return null;
  if (!/^\d*(\.\d{0,2})?$/.test(t)) return null;
  const v = Number(t);
  return Number.isFinite(v) ? Math.round(v * 100) : null;
}

/** Local guard only — the server re-validates every override. */
export function validateOverride(
  approvedCents: number | null,
  calculatedCents: number,
  normalCents: number,
  reason: string,
): { ok: boolean; error: string | null } {
  if (approvedCents === null) return { ok: false, error: "Enter an amount in dollars." };
  if (approvedCents < 0) return { ok: false, error: "The amount can't be negative." };
  if (approvedCents > normalCents) {
    return { ok: false, error: `The approved amount can't be more than the normal price (${money(normalCents)}).` };
  }
  if (approvedCents !== calculatedCents && reason.trim().length < 5) {
    return { ok: false, error: "A short internal reason is required when you change the calculated amount." };
  }
  return { ok: true, error: null };
}

async function call(body: Record<string, unknown>) {
  const { data, error } = await supabase.functions.invoke("admin-prorated-enrollment", { body });
  if (error) {
    const detail = (data as any)?.error ?? error.message;
    throw new Error(String(detail));
  }
  return data as any;
}

export async function previewProration(classId: string, sessionLabel: string): Promise<ProrationCalculation> {
  const res = await call({ action: "preview", class_id: classId, session_label: sessionLabel });
  return res.calculation as ProrationCalculation;
}

export async function createProratedAuthorization(input: {
  classId: string;
  sessionLabel: string;
  parentEmail: string;
  parentName?: string | null;
  approvedPriceCents?: number | null;
  overrideReason?: string;
  maxChildren?: number;
  expiresInDays?: number;
  adminNotes?: string | null;
}): Promise<ProratedAuthorization> {
  const res = await call({
    action: "create",
    class_id: input.classId,
    session_label: input.sessionLabel,
    parent_email: input.parentEmail,
    parent_name: input.parentName ?? null,
    approved_price_cents: input.approvedPriceCents ?? null,
    override_reason: input.overrideReason ?? "",
    max_children: input.maxChildren ?? 1,
    expires_in_days: input.expiresInDays ?? 7,
    admin_notes: input.adminNotes ?? null,
  });
  return res.authorization as ProratedAuthorization;
}

export async function listProratedAuthorizations(classId: string): Promise<ProratedAuthorization[]> {
  const res = await call({ action: "list", class_id: classId });
  return (res.authorizations ?? []) as ProratedAuthorization[];
}

export async function cancelProratedAuthorization(id: string): Promise<void> {
  await call({ action: "cancel", id });
}

export function authorizationStatusLabel(a: ProratedAuthorization): string {
  if (a.status === "used") return "Used";
  if (a.status === "cancelled") return "Cancelled";
  if (a.status === "reserved") return "Checkout in progress";
  if (a.status === "expired" || new Date(a.expires_at) <= new Date()) return "Expired";
  return "Approved · waiting for the family";
}
